import { supabase } from '../../../shared/services/supabase';

export interface LocationManufacturerSettings {
  id: string;
  location_id: string;
  manufacturer_id: string;
  min_order_qty: number | null;
  min_order_amt: number | null;
  free_ship_amt: number | null;
  preferred_ordering_day: number | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface LocationManufacturerSettingsWithDetails
  extends LocationManufacturerSettings {
  manufacturer: {
    id: string;
    name: string;
    min_order_qty: number | null;
    min_order_amt: number | null;
    free_ship_amt: number | null;
  };
}

export interface CreateLocationManufacturerSettingsInput {
  location_id: string;
  manufacturer_id: string;
  min_order_qty?: number | null;
  min_order_amt?: number | null;
  free_ship_amt?: number | null;
  preferred_ordering_day?: number | null;
  notes?: string | null;
}

export interface UpdateLocationManufacturerSettingsInput {
  id: string;
  min_order_qty?: number | null;
  min_order_amt?: number | null;
  free_ship_amt?: number | null;
  preferred_ordering_day?: number | null;
  notes?: string | null;
}

export const locationManufacturerSettingsService = {
  /**
   * Get all manufacturer settings for a location
   */
  async getLocationManufacturerSettings(
    location_id: string
  ): Promise<LocationManufacturerSettingsWithDetails[]> {
    const { data, error } = await supabase
      .from('location_manufacturer_settings')
      .select(
        `
        *,
        manufacturer:manufacturers (
          id,
          name,
          min_order_qty,
          min_order_amt,
          free_ship_amt
        )
      `
      )
      .eq('location_id', location_id)
      .order('manufacturer.name', { ascending: true });

    if (error) {
      console.error('Error fetching location manufacturer settings:', error);
      throw new Error(error.message);
    }

    return data as unknown as LocationManufacturerSettingsWithDetails[];
  },

  /**
   * Get specific location-manufacturer settings
   */
  async getLocationManufacturerSetting(
    location_id: string,
    manufacturer_id: string
  ): Promise<LocationManufacturerSettings | null> {
    const { data, error } = await supabase
      .from('location_manufacturer_settings')
      .select('*')
      .eq('location_id', location_id)
      .eq('manufacturer_id', manufacturer_id)
      .maybeSingle();

    if (error) {
      console.error('Error fetching location manufacturer setting:', error);
      throw new Error(error.message);
    }

    return data as LocationManufacturerSettings | null;
  },

  /**
   * Get effective minimum order quantity for a location-manufacturer pair
   * Returns location-specific override if exists, otherwise returns global manufacturer setting
   */
  async getEffectiveMinOrderQty(
    location_id: string,
    manufacturer_id: string
  ): Promise<number | null> {
    // First check for location-specific override
    const locationSetting = await this.getLocationManufacturerSetting(
      location_id,
      manufacturer_id
    );

    if (locationSetting && locationSetting.min_order_qty !== null) {
      return locationSetting.min_order_qty;
    }

    // Fall back to global manufacturer setting
    const { data: manufacturer, error } = await supabase
      .from('manufacturers')
      .select('min_order_qty')
      .eq('id', manufacturer_id)
      .single();

    if (error) {
      console.error('Error fetching manufacturer:', error);
      throw new Error(error.message);
    }

    // @ts-expect-error - Database types not regenerated yet
    return manufacturer.min_order_qty;
  },

  /**
   * Create location-manufacturer settings override
   */
  async createLocationManufacturerSettings(
    input: CreateLocationManufacturerSettingsInput
  ): Promise<LocationManufacturerSettings> {
    const { data, error } = await supabase
      .from('location_manufacturer_settings')
      // @ts-expect-error - Database types not regenerated yet
      .insert({
        location_id: input.location_id,
        manufacturer_id: input.manufacturer_id,
        min_order_qty: input.min_order_qty ?? null,
        min_order_amt: input.min_order_amt ?? null,
        free_ship_amt: input.free_ship_amt ?? null,
        preferred_ordering_day: input.preferred_ordering_day ?? null,
        notes: input.notes ?? null,
      })
      .select()
      .single();

    if (error) {
      console.error('Error creating location manufacturer settings:', error);
      throw new Error(error.message);
    }

    return data as LocationManufacturerSettings;
  },

  /**
   * Update location-manufacturer settings override
   */
  async updateLocationManufacturerSettings(
    input: UpdateLocationManufacturerSettingsInput
  ): Promise<LocationManufacturerSettings> {
    const updateData: Partial<LocationManufacturerSettings> = {};

    if (input.min_order_qty !== undefined)
      updateData.min_order_qty = input.min_order_qty;
    if (input.min_order_amt !== undefined)
      updateData.min_order_amt = input.min_order_amt;
    if (input.free_ship_amt !== undefined)
      updateData.free_ship_amt = input.free_ship_amt;
    if (input.preferred_ordering_day !== undefined)
      updateData.preferred_ordering_day = input.preferred_ordering_day;
    if (input.notes !== undefined) updateData.notes = input.notes;

    const { data, error } = await supabase
      .from('location_manufacturer_settings')
      // @ts-expect-error - Supabase type mismatch
      .update(updateData)
      .eq('id', input.id)
      .select()
      .single();

    if (error) {
      console.error('Error updating location manufacturer settings:', error);
      throw new Error(error.message);
    }

    return data as LocationManufacturerSettings;
  },

  /**
   * Delete location-manufacturer settings override (revert to global)
   */
  async deleteLocationManufacturerSettings(id: string): Promise<void> {
    const { error } = await supabase
      .from('location_manufacturer_settings')
      .delete()
      .eq('id', id);

    if (error) {
      console.error('Error deleting location manufacturer settings:', error);
      throw new Error(error.message);
    }
  },
};
